import murmurhash from 'murmurhash'

import { ConstraintModel } from '../compile/compilePattern'
import { BadgeSpec, ChartKey, Equality, OrderEdge, SeriesId, VarId, varId } from '../types'
import { SelectionResult } from './disjunctions'
import { CandleColor, candleColor } from './entailment'
import { GapObligation } from './gaps'

const PANE_RANGE = 100
const STRICT_MARGIN = 1.5
/**
 * Aesthetic-only separation for weak (≤/≥) comparisons. Satisfiability treats
 * them as permitting equality — but DRAWING them at exact equality collapses
 * every related series onto one point (a "Green Pressure ≤ Close/Open" pattern
 * rendered as a doji with the lines pinched into it). The preview should show
 * the representative satisfying state, not the knife-edge one.
 */
const WEAK_MARGIN = STRICT_MARGIN / 2
const WIGGLE = 1.2
const PROJECTION_PASSES = 80

export interface SolvedSeries {
  seriesId: SeriesId
  values: number[]
  /** true where the value came from a solved constraint (vs interpolation/wiggle) */
  pinned: boolean[]
}

export interface SolvedCandle {
  open: number
  high: number
  low: number
  close: number
  color: CandleColor
}

export interface SolvedChart {
  chartKey: ChartKey
  axisLength: number
  series: SolvedSeries[]
  candles: SolvedCandle[]
  badges: BadgeSpec[]
  seriesInfo: Record<
    SeriesId,
    { indicatorName: string; field: string; params?: Record<string, unknown> }
  >
}

/** Deterministic PRNG (mulberry32) seeded from the pattern id + chart key. */
const makeRandom = (seed: string): (() => number) => {
  let state = murmurhash.v3(seed)
  return () => {
    state |= 0
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const parseVarId = (
  id: VarId
): { kind: 'v' | 's'; chartKey: ChartKey; seriesId: SeriesId; bar: number } | undefined => {
  const parts = id.split('|')
  if (parts.length !== 4 || (parts[0] !== 'v' && parts[0] !== 's')) return undefined
  const bar = Number(parts[3])
  if (Number.isNaN(bar)) return undefined
  return { kind: parts[0] as 'v' | 's', chartKey: parts[1], seriesId: parts[2], bar }
}

const constantOf = (id: VarId): number | undefined => {
  if (!id.startsWith('k|')) return undefined
  const value = Number(id.slice(2))
  return Number.isNaN(value) ? undefined : value
}

/**
 * Turns a satisfiable selection into concrete drawable values: seeded natural
 * shapes, then bounded projection passes until every chosen order constraint
 * holds with a visible margin, clamped to graph intervals (spec §7.5).
 */
export const assignValues = (
  model: ConstraintModel,
  selection: SelectionResult,
  patternId: string,
  flatSeries?: Set<SeriesId>
): SolvedChart[] => {
  const graph = selection.graph
  if (!graph) return []

  // --- collect the chosen constraint set (base + picked disjunction options) ---
  const active = new Set(selection.chosen.conditionIds)
  const isActive = (provenance: { conditionId?: string }): boolean =>
    provenance.conditionId === undefined || active.has(provenance.conditionId)
  const edges: OrderEdge[] = model.edges.filter((edge) => isActive(edge.provenance))
  const equalities: Equality[] = model.equalities.filter((equality) =>
    isActive(equality.provenance)
  )
  const activeDisjunctions = model.disjunctions.filter((disjunction) =>
    isActive(disjunction.provenance)
  )
  activeDisjunctions.forEach((disjunction, i) => {
    const option = disjunction.options[selection.chosen.disjunctionPicks[i] ?? 0]
    if (!option) return
    edges.push(...option.edges)
    equalities.push(...option.equalities)
  })

  // Slope variables link to their parent series by sign: an interval proving
  // slope(S,b) > 0 becomes S(b) > S(b-1) so angle conditions shape the line.
  Object.entries(graph.intervals).forEach(([id, interval]) => {
    const parsed = parseVarId(id)
    if (!parsed || parsed.kind !== 's' || parsed.bar < 1) return
    const now = varId(parsed.chartKey, parsed.seriesId, parsed.bar)
    const prev = varId(parsed.chartKey, parsed.seriesId, parsed.bar - 1)
    if (interval.lo > 0) {
      edges.push({ gt: now, lt: prev, strict: true, provenance: { invariant: 'angle-sign' } })
    } else if (interval.hi < 0) {
      edges.push({ gt: prev, lt: now, strict: true, provenance: { invariant: 'angle-sign' } })
    }
  })

  const gapObligations = selection.gapResolution?.obligations ?? []

  // Variables a CONDITION references (as opposed to structural invariants,
  // which touch every bar of every band/candle). This is what "in use" means
  // for gray-until-it-matters rendering — invariants must not light bars up.
  const conditionTouched = new Set<VarId>()
  const touch = (id: VarId, provenance: { conditionId?: string }): void => {
    if (provenance.conditionId !== undefined) conditionTouched.add(id)
  }
  edges.forEach((edge) => {
    touch(edge.gt, edge.provenance)
    touch(edge.lt, edge.provenance)
  })
  equalities.forEach((equality) => {
    touch(equality.a, equality.provenance)
    touch(equality.b, equality.provenance)
  })
  model.gaps
    .filter((gap) => isActive(gap.provenance))
    .forEach((gap) => {
      touch(gap.aMinuend, gap.provenance)
      touch(gap.aSubtrahend, gap.provenance)
      if (gap.bMinuend) touch(gap.bMinuend, gap.provenance)
      if (gap.bSubtrahend) touch(gap.bSubtrahend, gap.provenance)
    })

  // --- values keyed by var id (constants resolve to themselves) ---
  const values = new Map<VarId, number>()
  const clampTo = (id: VarId, value: number): number => {
    const interval = graph.intervals[id]
    if (!interval) return value
    return Math.min(interval.hi, Math.max(interval.lo, value))
  }

  const constrainedVars = new Set<VarId>()
  edges.forEach((edge) => {
    constrainedVars.add(edge.gt)
    constrainedVars.add(edge.lt)
  })
  equalities.forEach((equality) => {
    constrainedVars.add(equality.a)
    constrainedVars.add(equality.b)
  })
  // Gap obligations (converging/diverging/changed) reference variables that
  // may appear in NO edge — e.g. 'Red Pressure converging Green Pressure'
  // where Red Pressure has no other conditions. Without seeding them the
  // obligation pass finds undefined values and silently no-ops, leaving the
  // line as pure wiggle that can drift apart while claiming to converge.
  gapObligations.forEach((obligation) => {
    if (obligation.kind === 'gapVsConst') {
      obligation.pair.forEach((id) => constrainedVars.add(id))
    } else if (obligation.kind === 'gapOrder') {
      obligation.biggerAbs.forEach((id) => constrainedVars.add(id))
      obligation.smallerAbs.forEach((id) => constrainedVars.add(id))
    } else {
      obligation.a.forEach((id) => constrainedVars.add(id))
      obligation.b.forEach((id) => constrainedVars.add(id))
    }
  })

  // Seed: constants exact; everything else mid-interval or mid-pane.
  constrainedVars.forEach((id) => {
    const constant = constantOf(id)
    if (constant !== undefined) {
      values.set(id, constant)
      return
    }
    const interval = graph.intervals[id]
    let seedValue = PANE_RANGE / 2
    if (interval) {
      const lo = Number.isFinite(interval.lo) ? interval.lo : undefined
      const hi = Number.isFinite(interval.hi) ? interval.hi : undefined
      if (lo !== undefined && hi !== undefined) seedValue = (lo + hi) / 2
      // Seed one margin above/below the bound — the MINIMAL visibly-satisfying
      // value. Extra padding here leaks into run-count renderings (Echo
      // 'greater than 0' must show 1 green square, not 3).
      else if (lo !== undefined) seedValue = lo + STRICT_MARGIN
      else if (hi !== undefined) seedValue = hi - STRICT_MARGIN
    }
    values.set(id, seedValue)
  })

  // --- projection passes ---
  for (let pass = 0; pass < PROJECTION_PASSES; pass += 1) {
    let violated = false
    equalities.forEach((equality) => {
      const a = values.get(equality.a)
      const b = values.get(equality.b)
      if (a === undefined || b === undefined) return
      if (a !== b) {
        const mid = (a + b) / 2
        if (constantOf(equality.a) === undefined) values.set(equality.a, clampTo(equality.a, mid))
        if (constantOf(equality.b) === undefined) values.set(equality.b, clampTo(equality.b, mid))
        violated = true
      }
    })
    edges.forEach((edge) => {
      const gtValue = values.get(edge.gt)
      const ltValue = values.get(edge.lt)
      if (gtValue === undefined || ltValue === undefined) return
      const margin = edge.strict ? STRICT_MARGIN : WEAK_MARGIN
      const shortfall = ltValue + margin - gtValue
      if (shortfall <= 0) return
      violated = true
      const gtFixed = constantOf(edge.gt) !== undefined
      const ltFixed = constantOf(edge.lt) !== undefined
      if (gtFixed && ltFixed) return
      if (gtFixed) {
        values.set(edge.lt, clampTo(edge.lt, ltValue - shortfall))
      } else if (ltFixed) {
        values.set(edge.gt, clampTo(edge.gt, gtValue + shortfall))
      } else {
        values.set(edge.gt, clampTo(edge.gt, gtValue + shortfall / 2 + 0.01))
        values.set(edge.lt, clampTo(edge.lt, ltValue - shortfall / 2 - 0.01))
      }
    })

    // Gap obligations from converging/diverging/changed conditions shape the
    // drawn separations — without this the chart could show lines diverging
    // for a `converging` condition even though the solve was satisfiable.
    const setSeparation = (pair: [VarId, VarId], separation: number): boolean => {
      const [minuendId, subtrahendId] = pair
      const minuend = values.get(minuendId)
      const subtrahend = values.get(subtrahendId)
      if (minuend === undefined || subtrahend === undefined) return false
      const sign = minuend - subtrahend >= 0 ? 1 : -1
      const mid = (minuend + subtrahend) / 2
      const minuendFixed = constantOf(minuendId) !== undefined
      const subtrahendFixed = constantOf(subtrahendId) !== undefined
      if (minuendFixed && subtrahendFixed) return false
      if (minuendFixed) {
        values.set(subtrahendId, clampTo(subtrahendId, minuend - sign * separation))
      } else if (subtrahendFixed) {
        values.set(minuendId, clampTo(minuendId, subtrahend + sign * separation))
      } else {
        values.set(minuendId, clampTo(minuendId, mid + (sign * separation) / 2))
        values.set(subtrahendId, clampTo(subtrahendId, mid - (sign * separation) / 2))
      }
      return true
    }
    const absGap = (pair: [VarId, VarId]): number | undefined => {
      const minuend = values.get(pair[0])
      const subtrahend = values.get(pair[1])
      if (minuend === undefined || subtrahend === undefined) return undefined
      return Math.abs(minuend - subtrahend)
    }
    gapObligations.forEach((obligation: GapObligation) => {
      if (obligation.kind === 'gapVsConst') {
        const gap = absGap(obligation.pair)
        if (gap === undefined) return
        let target: number | undefined
        if (obligation.rel === '=') target = obligation.constant
        else if (obligation.rel === '>' && gap <= obligation.constant) {
          target = obligation.constant + STRICT_MARGIN
        } else if (obligation.rel === '<' && gap >= obligation.constant) {
          // Proportional so a small threshold never collapses the two lines
          // onto each other (and target stays strictly below the constant).
          target = obligation.constant * 0.6
        }
        if (target === undefined || Math.abs(gap - target) < 1e-9) return
        if (setSeparation(obligation.pair, target)) violated = true
      } else if (obligation.kind === 'gapOrder') {
        let bigger = absGap(obligation.biggerAbs)
        let smaller = absGap(obligation.smallerAbs)
        if (bigger === undefined || smaller === undefined) return
        // Keep the converged gap visibly non-zero (PDS's converging also
        // rejects an exact-zero gap) — the lines should approach, not fuse.
        if (smaller < WEAK_MARGIN / 2) {
          if (setSeparation(obligation.smallerAbs, WEAK_MARGIN)) violated = true
          smaller = WEAK_MARGIN
        }
        bigger = absGap(obligation.biggerAbs) ?? bigger
        if (bigger > smaller + STRICT_MARGIN / 2) return
        if (setSeparation(obligation.biggerAbs, smaller + STRICT_MARGIN)) violated = true
      }
      // sameSign is maintained implicitly: setSeparation preserves each
      // pair's current sign and the graph solve already fixed the signs.
    })
    if (!violated) break
  }

  // --- build per-chart drawable series ---
  return Object.entries(model.charts).map(([chartKey, chartInfo]) => {
    const random = makeRandom(`${patternId}|${chartKey}`)
    const wiggle = (): number => (random() - 0.5) * 2 * WIGGLE

    const solvedAt = (seriesId: SeriesId, bar: number): number | undefined =>
      values.get(varId(chartKey, seriesId, bar))

    const buildSeries = (
      seriesId: SeriesId,
      baseLevel: number
    ): { values: number[]; pinned: boolean[] } => {
      const flat = flatSeries?.has(seriesId) ?? false
      const result: number[] = new Array(chartInfo.axisLength).fill(NaN)
      const pinned: boolean[] = new Array(chartInfo.axisLength).fill(false)
      for (let bar = 0; bar < chartInfo.axisLength; bar += 1) {
        const solved = solvedAt(seriesId, bar)
        if (solved !== undefined) {
          result[bar] = solved
          // Only CONDITION-referenced bars count as pinned for rendering —
          // structural invariants solve values on every bar of a band but
          // must not defeat gray-until-it-matters.
          pinned[bar] = conditionTouched.has(varId(chartKey, seriesId, bar))
        }
      }
      const anySolved = result.some((value) => !Number.isNaN(value))
      if (!anySolved) {
        let level = baseLevel
        for (let bar = 0; bar < chartInfo.axisLength; bar += 1) {
          result[bar] = flat ? baseLevel : level
          if (!flat) level += wiggle()
        }
        return { values: result, pinned }
      }
      // Fill gaps by interpolating between solved anchors (flat hold for levels).
      let previousIndex = -1
      for (let bar = 0; bar <= chartInfo.axisLength; bar += 1) {
        const isAnchor = bar < chartInfo.axisLength && !Number.isNaN(result[bar])
        if (!isAnchor && bar < chartInfo.axisLength) continue
        const nextIndex = bar < chartInfo.axisLength ? bar : -1
        const start = previousIndex
        const end = nextIndex
        const startValue = start >= 0 ? result[start] : end >= 0 ? result[end] : baseLevel
        const endValue = end >= 0 ? result[end] : startValue
        const from = start + 1
        const to = end >= 0 ? end - 1 : chartInfo.axisLength - 1
        for (let gap = from; gap <= to; gap += 1) {
          if (gap < 0 || gap >= chartInfo.axisLength) continue
          const span = end >= 0 && start >= 0 ? end - start : 1
          const t = end >= 0 && start >= 0 ? (gap - start) / span : 1
          const base = startValue + (endValue - startValue) * t
          result[gap] = flat ? base : base + wiggle()
        }
        previousIndex = bar
      }
      return { values: result, pinned }
    }

    const indicatorSeriesIds = chartInfo.seriesIds.filter(
      (seriesId) => !seriesId.startsWith('candle.')
    )
    const series: SolvedSeries[] = indicatorSeriesIds.map((seriesId, i) => ({
      seriesId,
      ...buildSeries(seriesId, 40 + ((i * 12) % 40)),
    }))

    // Candles: solved fields pinned, the rest a neutral walk around mid-pane.
    const closeValues = buildSeries('candle.close', PANE_RANGE / 2 + 5).values
    const openValues: number[] = new Array(chartInfo.axisLength)
    const highValues: number[] = new Array(chartInfo.axisLength)
    const lowValues: number[] = new Array(chartInfo.axisLength)
    const candles: SolvedCandle[] = []
    for (let bar = 0; bar < chartInfo.axisLength; bar += 1) {
      const solvedOpen = solvedAt('candle.open', bar)
      const solvedClose = solvedAt('candle.close', bar)
      const close = solvedClose ?? closeValues[bar]
      const open =
        solvedOpen ??
        (bar > 0 ? (candles[bar - 1]?.close ?? close) + wiggle() * 0.5 : close + wiggle())
      const bodyHigh = Math.max(open, close)
      const bodyLow = Math.min(open, close)
      const high = Math.max(
        solvedAt('candle.high', bar) ?? -Infinity,
        bodyHigh + Math.abs(wiggle()) * 0.6 + 0.2
      )
      const low = Math.min(
        solvedAt('candle.low', bar) ?? Infinity,
        bodyLow - Math.abs(wiggle()) * 0.6 - 0.2
      )
      openValues[bar] = open
      highValues[bar] = high
      lowValues[bar] = low
      // Entailed direction wins (green/red); otherwise a candle some condition
      // references — current-candle or an offset target — renders WHITE to
      // show it is in use; untouched candles stay gray.
      const entailed = candleColor(graph, chartKey, bar)
      const inUse = ['open', 'high', 'low', 'close'].some((field) =>
        conditionTouched.has(varId(chartKey, `candle.${field}`, bar))
      )
      candles.push({
        open,
        high,
        low,
        close,
        color: entailed === 'gray' && inUse ? 'white' : entailed,
      })
    }

    return {
      chartKey,
      axisLength: chartInfo.axisLength,
      series,
      candles,
      badges: model.badges.filter((badge) => badge.chartKey === chartKey),
      seriesInfo: chartInfo.seriesInfo,
    }
  })
}
